import { Component, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { EmployeeNotificationSettingsDto } from '../../../../api/gen';
import { NotificationSettingsDataService } from '../../../services/data/notification-settings-data.service';
import { notificationChannels } from '../../notification-channels';

@Component({
  selector: 'app-edit-notification-settings-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './edit-notification-settings-dialog.component.html',
  styleUrl: './edit-notification-settings-dialog.component.scss',
})
export class EditNotificationSettingsDialogComponent {
  private readonly initial =
    inject<EmployeeNotificationSettingsDto>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(
    MatDialogRef<EditNotificationSettingsDialogComponent>,
  );
  private readonly settings = inject(NotificationSettingsDataService);
  private readonly snackbar = inject(MatSnackBar);
  protected readonly channels = notificationChannels;
  protected readonly saving = signal(false);
  protected readonly failed = signal(false);
  protected readonly form = new FormGroup({
    emailEnabled: new FormControl(this.initial.emailEnabled ?? true, {
      nonNullable: true,
    }),
    smsEnabled: new FormControl(this.initial.smsEnabled ?? true, {
      nonNullable: true,
    }),
    notificationCenterEnabled: new FormControl(
      this.initial.notificationCenterEnabled ?? true,
      { nonNullable: true },
    ),
  });
  private readonly value = toSignal(this.form.valueChanges, {
    initialValue: this.form.getRawValue(),
  });
  protected readonly allDisabled = computed(() => {
    const value = this.value();
    return this.channels.every(({ key }) => !value[key]);
  });
  protected readonly changed = computed(() => {
    const value = this.value();
    return this.channels.some(
      ({ key }) => value[key] !== (this.initial[key] ?? true),
    );
  });

  protected save(): void {
    if (this.saving() || !this.changed()) return;
    this.saving.set(true);
    this.failed.set(false);
    this.dialogRef.disableClose = true;
    this.form.disable({ emitEvent: false });
    this.settings
      .update(this.form.getRawValue())
      .pipe(
        finalize(() => {
          this.saving.set(false);
          this.dialogRef.disableClose = false;
          this.form.enable({ emitEvent: false });
        }),
      )
      .subscribe({
        next: () => {
          this.snackbar.open(
            $localize`:@@notificationSettingsSaved:Benachrichtigungseinstellungen gespeichert.`,
            undefined,
            { duration: 4000 },
          );
          this.dialogRef.close(true);
        },
        error: () => this.failed.set(true),
      });
  }
}
